/**
 * Handles composite emojis, skin tones, and other complex Unicode sequences.
 *
 * @param {string} str - The string to get the first character from.
 * @returns {string} The first grapheme cluster of the string.
 */
const getFirstCharacter = (str: string): string => {
  if (!str) {
    return '';
  }

  if ('Segmenter' in Intl) {
    const segmenter = new (Intl as any).Segmenter('en', { granularity: 'grapheme' });
    const segments = segmenter.segment(str);
    const firstSegment = segments[Symbol.iterator]().next();
    return firstSegment.done ? '' : firstSegment.value.segment;
  }

  // Fallback for older browsers if Segmenter is not available
  return Array.from(str)[0] || '';
};

/**
 * Extracts initials from a name, handling complex Unicode characters.
 *
 * @param {string} name - The name to extract initials from.
 * @param {boolean} [singleCharacterOnly=false] - Whether to return only the first character.
 * @returns {string} The extracted initials or single character.
 */
export const getInitials = (name: string, singleCharacterOnly = false) => {
  if (name.length <= 1) {
    return name;
  }

  const trimmedName = name.trim();

  const firstChar = getFirstCharacter(trimmedName);

  if (singleCharacterOnly) {
    return firstChar;
  }

  if (firstChar === trimmedName) {
    return trimmedName;
  }

  const parts = trimmedName.split(/\s/);
  const lastChar = parts.length > 1 ? getFirstCharacter(parts[parts.length - 1]) : '';

  return `${firstChar}${lastChar}`;
};
